import type { ApiGetListingsPayload } from 'src/types/listing';
import type { ApiRentalListingListParams } from 'src/types/rental';

import { omit, omitBy, isUndefined } from 'lodash';

import { Fuel, Gearbox, BodyType } from 'src/types/car';

/**
 * The catalog's address, in one place (storefront-catalog spec §4.1, decision №26): the address is
 * the only state of a catalog — make and model in the path, every other facet and the page in the
 * query — so a link carries what the person was looking at, and back, forward and reload keep it.
 *
 *   /buy/bmw/x5?priceTo=30000&yearFrom=2018&page=2      the sale catalog
 *   /buy?body=suv&fuel=diesel&gearbox=automatic         the car's own attributes, on both catalogs
 *   /rent/toyota?dailyTo=150                            the rent catalog
 *   /dealer/<id>?make=bmw&page=2&vertical=rent          a dealer's page: its path is taken, so the
 *                                                       make and the model go to the query
 *
 * Everything that reads the address goes through `parseCatalogSearch`, and nothing from the query
 * reaches the api without it: a number that is not a number, a negative price, a page of zero is
 * dropped here rather than sent.
 *
 * The make and the model in an address are the catalogue's own slugs — a field of the api since
 * batch 2b (`GET /common/makes?slug=`), no longer computed here. This module carries them; it does
 * not make them.
 */

export type CatalogVertical = 'buy' | 'rent';

/**
 * Cars on one catalog page — twelve fill whole rows at two, three and four columns. The only place
 * the number lives in `src`; the e2e suite mirrors it as `FEED_PAGE_SIZE` (`e2e/src/api.ts`).
 */
export const CATALOG_PAGE_SIZE = 12;

/** A VIN is 17 characters; the address keeps a little more, and nothing past this. */
const VIN_MAX_LENGTH = 32;

// eslint-disable-next-line no-control-regex -- the very characters this is here to take out
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g;

/**
 * The facets of both verticals; each vertical reads and writes only its own. A type, not an
 * interface, so it passes where a plain record of values is expected (`hasActiveFilter`).
 */
export type CatalogFilterValues = {
  yearFrom?: number;
  yearTo?: number;
  priceFrom?: number;
  priceTo?: number;
  vin?: string;
  dailyFrom?: number;
  dailyTo?: number;
  /**
   * The car itself — body, gearbox, fuel (decision №37): facets of both verticals. Their values are
   * spelled in the address as the api spells them (`?body=suv&gearbox=automatic&fuel=plug-in-hybrid`);
   * only the body's own name differs — `body` here, `bodyType` in what the api is asked.
   */
  body?: BodyType;
  gearbox?: Gearbox;
  fuel?: Fuel;
};

type NumberFacet = 'yearFrom' | 'yearTo' | 'priceFrom' | 'priceTo' | 'dailyFrom' | 'dailyTo';

const NUMBER_FACETS: Record<CatalogVertical, readonly NumberFacet[]> = {
  buy: ['yearFrom', 'yearTo', 'priceFrom', 'priceTo'],
  rent: ['dailyFrom', 'dailyTo'],
};

/** The api counts years in whole numbers (`@IsInt` on `yearFrom`/`yearTo`); a fraction would be a 400. */
const WHOLE_NUMBER_FACETS: ReadonlySet<NumberFacet> = new Set(['yearFrom', 'yearTo']);

/** The values an address may name for each attribute: the api's own lists, and nothing else. */
const BODY_TYPES = Object.values(BodyType);
const GEARBOXES = Object.values(Gearbox);
const FUELS = Object.values(Fuel);

export interface CatalogSearch {
  filters: CatalogFilterValues;
  page: number;
}

/** A finite number, zero or more — anything else (`abc`, `-5`, `Infinity`, an empty value) is absent. */
const readNumber = (raw: string | null, whole: boolean): number | undefined => {
  if (raw === null || raw.trim() === '') return undefined;

  const value = Number(raw);

  if (!Number.isFinite(value) || value < 0) return undefined;
  if (whole && !Number.isInteger(value)) return undefined;

  return value;
};

/**
 * One of the values the api knows — read without regard to case or stray spaces, so a hand-typed
 * `?body=SUV` still finds its cars. Anything else (`tank`, an empty value) is absent: the api answers
 * a value it does not know with a 400, and the address must never be the reason for one.
 */
const readChoice = <T extends string>(raw: string | null, known: readonly T[]): T | undefined => {
  const value = raw?.trim().toLowerCase();

  return known.find((entry) => entry === value);
};

/** The last page an address may ask for: past it the offset sent to the api would stop being an exact number. */
const MAX_PAGE = Math.floor(Number.MAX_SAFE_INTEGER / CATALOG_PAGE_SIZE);

/** A whole page number from one up; a missing, fractional, zero, negative or absurd page is the first. */
const readPage = (raw: string | null): number => {
  const value = raw === null || raw.trim() === '' ? NaN : Number(raw);

  return Number.isSafeInteger(value) && value >= 1 && value <= MAX_PAGE ? value : 1;
};

/**
 * The facets and the page a catalog address asks for. Only the vertical's own facets are read;
 * anything else in the query — a facet of the other vertical, a parameter nobody knows — is ignored.
 */
export const parseCatalogSearch = (searchParams: URLSearchParams, vertical: CatalogVertical): CatalogSearch => {
  const filters: CatalogFilterValues = {};

  for (const facet of NUMBER_FACETS[vertical]) {
    const value = readNumber(searchParams.get(facet), WHOLE_NUMBER_FACETS.has(facet));

    if (value !== undefined) filters[facet] = value;
  }

  if (vertical === 'buy') {
    // No control character belongs to a VIN, and one in the address (`?vin=%00`) is not passed on.
    const vin = (searchParams.get('vin') ?? '')
      .replace(CONTROL_CHARACTERS, '')
      .trim()
      .slice(0, VIN_MAX_LENGTH);

    if (vin) filters.vin = vin;
  }

  const body = readChoice(searchParams.get('body'), BODY_TYPES);
  const gearbox = readChoice(searchParams.get('gearbox'), GEARBOXES);
  const fuel = readChoice(searchParams.get('fuel'), FUELS);

  if (body) filters.body = body;
  if (gearbox) filters.gearbox = gearbox;
  if (fuel) filters.fuel = fuel;

  return { filters, page: readPage(searchParams.get('page')) };
};

/** Plain code-unit order: the same on every machine, unlike a locale-aware comparison. */
const byCodeUnits = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export interface CatalogLocation {
  vertical: CatalogVertical;
  makeSlug?: string;
  /** Only with a make: a model on its own has no place in the address. */
  modelSlug?: string;
  filters?: CatalogFilterValues;
  page?: number;
  /** The dealer's page (`/dealer/:sellerId`): the path is the dealer's, the make and the model go to the query. */
  dealerId?: string;
}

/**
 * The address of a catalog view. Empty facets and the first page are left out, and the query is in
 * alphabetical order, so one view has exactly one address — the one a person shares and a search
 * engine indexes.
 */
export const buildCatalogPath = ({
  vertical,
  makeSlug,
  modelSlug,
  filters = {},
  page = 1,
  dealerId,
}: CatalogLocation): string => {
  const query: [string, string][] = [];

  for (const facet of NUMBER_FACETS[vertical]) {
    const value = filters[facet];

    if (value !== undefined) query.push([facet, String(value)]);
  }
  if (vertical === 'buy' && filters.vin) query.push(['vin', filters.vin]);
  if (filters.body) query.push(['body', filters.body]);
  if (filters.gearbox) query.push(['gearbox', filters.gearbox]);
  if (filters.fuel) query.push(['fuel', filters.fuel]);
  if (page > 1) query.push(['page', String(page)]);

  let path: string;

  if (dealerId) {
    if (makeSlug) query.push(['make', makeSlug]);
    if (makeSlug && modelSlug) query.push(['model', modelSlug]);
    // No parameter is the sale: the address in the cabinet's QR code opens it, as it always did.
    if (vertical === 'rent') query.push(['vertical', 'rent']);
    path = `/dealer/${encodeURIComponent(dealerId)}`;
  } else {
    const segments = makeSlug ? [makeSlug, ...(modelSlug ? [modelSlug] : [])] : [];

    path = ['', vertical, ...segments.map((segment) => encodeURIComponent(segment))].join('/');
  }

  query.sort(([a], [b]) => byCodeUnits(a, b));
  const search = new URLSearchParams(query).toString();

  return search ? `${path}?${search}` : path;
};

/** Drops the keys whose value is `undefined`, so a request carries only what was asked. */
const defined = <T extends object>(value: T): T => omitBy(value, isUndefined) as T;

const offsetOf = (page: number): number => (Math.max(1, page) - 1) * CATALOG_PAGE_SIZE;

export interface CatalogQuery {
  makeId?: string;
  modelId?: string;
  filters?: CatalogFilterValues;
  page?: number;
}

/** What `GET /buyer/listings` is asked for one page of the sale catalog. */
export const toSaleParams = ({ makeId, modelId, filters = {}, page = 1 }: CatalogQuery): ApiGetListingsPayload =>
  defined({
    makeId,
    modelId,
    yearFrom: filters.yearFrom,
    yearTo: filters.yearTo,
    priceFrom: filters.priceFrom,
    priceTo: filters.priceTo,
    vin: filters.vin,
    // The address says `body`; the api's parameter is `bodyType`.
    bodyType: filters.body,
    gearbox: filters.gearbox,
    fuel: filters.fuel,
    limit: CATALOG_PAGE_SIZE,
    offset: offsetOf(page),
  });

/**
 * What `GET /buyer/rentals` is asked for one page of the rent catalog. The api pages rentals the
 * way it pages sale — `limit` and `offset` — although its answer carries only `total`.
 */
export const toRentalParams = ({ makeId, modelId, filters = {}, page = 1 }: CatalogQuery): ApiRentalListingListParams =>
  defined({
    makeId,
    modelId,
    minDailyPrice: filters.dailyFrom,
    maxDailyPrice: filters.dailyTo,
    bodyType: filters.body,
    gearbox: filters.gearbox,
    fuel: filters.fuel,
    limit: CATALOG_PAGE_SIZE,
    offset: offsetOf(page),
  });

/** What a facets endpoint takes: a feed's own filters, less the make, the model and the page. */
export type CatalogFacetsParams =
  | Omit<ApiGetListingsPayload, 'makeId' | 'modelId' | 'limit' | 'offset'>
  | Omit<ApiRentalListingListParams, 'makeId' | 'modelId' | 'limit' | 'offset'>;

/**
 * What `GET /buyer/listings/facets` is asked: the sale catalog's filters without the make, the model
 * and the page. Built from `toSaleParams`, so a filter the feed learns is a filter its counts follow.
 */
export const toSaleFacetsParams = (filters: CatalogFilterValues = {}): CatalogFacetsParams =>
  omit(toSaleParams({ filters }), ['limit', 'offset']);

/** The same for `GET /buyer/rentals/facets`. */
export const toRentalFacetsParams = (filters: CatalogFilterValues = {}): CatalogFacetsParams =>
  omit(toRentalParams({ filters }), ['limit', 'offset']);

/** How many pages `total` cars fill — one at least, so an empty catalog is on its first page. */
export const pageCount = (total: number): number => Math.max(1, Math.ceil(total / CATALOG_PAGE_SIZE));

/** The page to show for an address that asks for `page`: past the last page is the last page. */
export const clampPage = (page: number, total: number): number => Math.min(Math.max(1, page), pageCount(total));
